import React, { useEffect, useMemo, useState } from 'react';
import jsPDF from 'jspdf';
import { supabase } from '../../lib/supabase';
import { useSidebar } from '../../contexts/SidebarContext';
import Sidebar from '../../components/ui/Sidebar';
import TopBar from '../../components/ui/TopBar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const inputClass =
  'w-full px-3 py-2 bg-background border border-border rounded-md text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors';

const tabButtonClass = (active) =>
  `inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium border transition-colors ${
    active
      ? 'bg-primary/10 text-primary border-primary/20'
      : 'bg-card text-foreground border-border hover:bg-muted/30'
  }`;

const formatCurrency = (value, currencyCode = 'EUR') => {
  try {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: currencyCode || 'EUR' }).format(value ?? 0);
  } catch {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value ?? 0);
  }
};

const formatDate = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('fr-FR', { year: 'numeric', month: 'short', day: 'numeric' });
};

function getTierColor(tier) {
  switch (tier) {
    case 'bronze':
      return 'bg-amber-700 text-white';
    case 'silver':
      return 'bg-gray-400 text-gray-900';
    case 'gold':
      return 'bg-yellow-500 text-gray-900';
    case 'platinum':
      return 'bg-purple-600 text-white';
    default:
      return 'bg-gray-500 text-white';
  }
}

const DentistBillingPackPage = () => {
  const { isCollapsed, toggleSidebar } = useSidebar();
  const [tab, setTab] = useState('packs'); // 'invoices' | 'packs' (par défaut: packs)

  // ── user ────────────────────────────────────────────────────────────────
  const [currentUser, setCurrentUser] = useState(null);
  const [dentistCountryId, setDentistCountryId] = useState(null);
  const [currencyCode, setCurrencyCode] = useState('EUR');

  // ── factures ────────────────────────────────────────────────────────────
  const [loadingInvoices, setLoadingInvoices] = useState(true);
  const [casesData, setCasesData] = useState([]);
  const [filters, setFilters] = useState({ search: '', dateFrom: '', dateTo: '' });

  // ── packs ───────────────────────────────────────────────────────────────
  const [loadingPacks, setLoadingPacks] = useState(true);
  const [packsError, setPacksError] = useState(null);
  const [packages, setPackages] = useState([]);
  const [expandedPackageIds, setExpandedPackageIds] = useState(() => new Set());

  const breadcrumbItems = useMemo(
    () => [
      { label: 'Espace dentiste', path: '/dentiste', icon: 'LayoutGrid' },
      { label: 'Factures & Packs', path: '/dentiste/facturation-pack', icon: 'Receipt' },
    ],
    [],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.auth.getUser();
        if (error) throw error;
        if (cancelled) return;
        setCurrentUser(data?.user || null);
      } catch (e) {
        console.error('Error fetching auth user:', e);
        if (!cancelled) setCurrentUser(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!currentUser?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('countries(id, currency_code)')
          .eq('id', currentUser.id)
          .maybeSingle();
        if (error) throw error;
        if (cancelled) return;
        const cId = data?.countries?.id || null;
        const cur = data?.countries?.currency_code || null;
        if (cId) setDentistCountryId(cId);
        if (cur) setCurrencyCode(cur);
      } catch (e) {
        console.error('Error fetching dentist country:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser?.id]);

  const fetchCasesData = async (dentistId) => {
    try {
      setLoadingInvoices(true);

      const { data: cases, error: casesError } = await supabase
        .from('cases')
        .select(
          `
          id,
          case_number,
          status,
          created_date,
          patient:patients(
            id,
            name,
            patient_code
          ),
          dentist:user_profiles!cases_user_profile_id_fkey(
            id,
            full_name,
            email,
            country:countries(
              id,
              country_name,
              currency_code
            )
          )
        `,
        )
        .eq('user_profile_id', dentistId)
        .order('created_date', { ascending: false });

      if (casesError) throw casesError;

      const casesWithDetails = await Promise.all(
        (cases || []).map(async (caseItem) => {
          const { data: phaseInvoices, error: piErr } = await supabase
            .from('case_phase_invoices')
            .select(
              `
              id,
              phase_number,
              phase_name,
              phase_price,
              status,
              billing_date,
              invoice:invoices(
                id,
                invoice_number,
                issue_date,
                due_date,
                payment_date,
                subtotal,
                total_amount,
                amount_paid,
                balance_due,
                discount_amount,
                status,
                notes,
                payment_method
              )
            `,
            )
            .eq('case_id', caseItem.id)
            .order('phase_number');
          if (piErr) throw piErr;

          // Base filter (business requirement):
          // - phases: invoiced|paid
          // - invoice exists
          // - invoice.status = pending (Envoyée)
          const kept = (phaseInvoices || []).filter(
            (p) =>
              (p.status === 'invoiced' || p.status === 'paid') &&
              !!p.invoice &&
              p.invoice.status === 'pending',
          );

          return {
            ...caseItem,
            phaseInvoices: kept,
            currencyCode: caseItem?.dentist?.country?.currency_code || currencyCode || 'EUR',
          };
        }),
      );

      const nonEmpty = casesWithDetails.filter((c) => (c.phaseInvoices || []).length > 0);
      setCasesData(nonEmpty);

      // Fallback country/currency from first case if missing.
      if (!dentistCountryId && nonEmpty[0]?.dentist?.country?.id) setDentistCountryId(nonEmpty[0].dentist.country.id);
      if (!currencyCode && nonEmpty[0]?.dentist?.country?.currency_code) setCurrencyCode(nonEmpty[0].dentist.country.currency_code);
    } catch (e) {
      console.error('Error fetching invoices data:', e);
      setCasesData([]);
    } finally {
      setLoadingInvoices(false);
    }
  };

  useEffect(() => {
    if (!currentUser?.id) return;
    fetchCasesData(currentUser.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id]);

  const filteredInvoiceRows = useMemo(() => {
    const searchLower = filters.search.trim().toLowerCase();
    const from = filters.dateFrom ? new Date(filters.dateFrom) : null;
    const to = filters.dateTo ? new Date(filters.dateTo) : null;

    const casesFiltered = (casesData || []).filter((c) => {
      if (searchLower) {
        const hit =
          c.case_number?.toLowerCase().includes(searchLower) ||
          c.patient?.name?.toLowerCase().includes(searchLower) ||
          c.patient?.patient_code?.toLowerCase().includes(searchLower) ||
          (c.phaseInvoices || []).some((p) =>
            String(p?.invoice?.invoice_number || '').toLowerCase().includes(searchLower),
          );
        if (!hit) return false;
      }
      if (from && new Date(c.created_date) < from) return false;
      if (to && new Date(c.created_date) > to) return false;
      return true;
    });

    return casesFiltered.flatMap((caseItem) =>
      (caseItem.phaseInvoices || []).map((phase) => ({
        caseId: caseItem.id,
        caseNumber: caseItem.case_number,
        caseCreatedDate: caseItem.created_date,
        patientName: caseItem.patient?.name || '—',
        patientCode: caseItem.patient?.patient_code || '—',
        phaseNumber: phase.phase_number,
        phaseName: phase.phase_name || '—',
        phaseStatus: phase.status,
        invoice: phase.invoice,
        currency: caseItem.currencyCode || currencyCode || 'EUR',
      })),
    );
  }, [casesData, filters, currencyCode]);

  const handleDownloadInvoicePdf = (invoice, row) => {
    // Minimal, adapted from DentistInvoicesTracking (jsPDF)
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const W = 210;
    const margin = 14;

    const fmt = (val) => {
      const formatted = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: row.currency }).format(val ?? 0);
      return formatted.replace(/\u202f/g, ' ').replace(/\u00a0/g, ' ');
    };
    const fmtDate = (d) =>
      d ? new Date(d).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' }) : '—';

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text('FACTURE', margin, 20);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`N°: ${invoice?.invoice_number || '—'}`, margin, 30);
    doc.text(`Date d'émission: ${fmtDate(invoice?.issue_date)}`, margin, 36);
    doc.text(`Date d'échéance: ${fmtDate(invoice?.due_date)}`, margin, 42);

    doc.setFont('helvetica', 'bold');
    doc.text(`Cas: ${row.caseNumber}`, margin, 54);
    doc.setFont('helvetica', 'normal');
    doc.text(`Patient: ${row.patientName}`, margin, 60);
    doc.text(`Phase: ${row.phaseNumber} — ${row.phaseName}`, margin, 66);

    doc.setDrawColor(229, 231, 235);
    doc.line(margin, 74, W - margin, 74);

    doc.setFont('helvetica', 'bold');
    doc.text('Total', margin, 86);
    doc.text(fmt(invoice?.total_amount), W - margin, 86, { align: 'right' });

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('Statut: ENVOYÉE', margin, 96);

    doc.save(`Facture_${invoice?.invoice_number || '—'}.pdf`);
  };

  const loadPackagesForCountry = async (countryId) => {
    try {
      setLoadingPacks(true);
      setPacksError(null);
      let query = supabase
        .from('commercial_packages')
        .select(
          `
          *,
          countries ( id, country_name, country_code, currency_code ),
          package_services ( id, quantity, service:services ( id, service_name, service_code, description, category, unit_price ) )
        `,
        )
        .eq('status', 'active')
        .order('tier', { ascending: true })
        .order('base_price', { ascending: true });
      if (countryId) query = query.eq('country_id', countryId);
      const { data, error } = await query;
      if (error) throw error;
      const pkgs = data || [];
      setPackages(pkgs);
      setExpandedPackageIds(new Set(pkgs.map((p) => p.id)));
    } catch (e) {
      console.error('Error fetching packages:', e);
      setPackages([]);
      setPacksError(e?.message || 'Échec de la récupération des packs');
    } finally {
      setLoadingPacks(false);
    }
  };

  useEffect(() => {
    if (tab !== 'packs') return;
    loadPackagesForCountry(dentistCountryId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, dentistCountryId]);

  const toggleExpanded = (pkgId) => {
    setExpandedPackageIds((prev) => {
      const next = new Set(prev);
      if (next.has(pkgId)) next.delete(pkgId);
      else next.add(pkgId);
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isCollapsed={isCollapsed} onToggle={toggleSidebar} />
      <TopBar sidebarCollapsed={isCollapsed} onSidebarToggle={toggleSidebar} />

      <main className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Breadcrumb items={breadcrumbItems} />

        <div className="px-6 py-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Factures & Packs</h1>
              <p className="text-muted-foreground">
                Factures envoyées (statut Envoyé) et packs disponibles pour votre pays.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button type="button" className={tabButtonClass(tab === 'packs')} onClick={() => setTab('packs')}>
                <Icon name="Package" size={16} />
                Packs disponibles
              </button>
              <button type="button" className={tabButtonClass(tab === 'invoices')} onClick={() => setTab('invoices')}>
                <Icon name="Receipt" size={16} />
                Factures
              </button>
            </div>
          </div>

          {tab === 'invoices' && (
            <div className="space-y-4">
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <input
                    className={inputClass}
                    value={filters.search}
                    onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                    placeholder="Rechercher par N° cas, patient, code patient…"
                  />
                  <input
                    className={inputClass}
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value }))}
                  />
                  <input
                    className={inputClass}
                    type="date"
                    value={filters.dateTo}
                    onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value }))}
                  />
                </div>
                <div className="flex justify-end mt-3">
                  <Button
                    variant="outline"
                    iconName="RefreshCw"
                    iconPosition="left"
                    onClick={() => currentUser?.id && fetchCasesData(currentUser.id)}
                    disabled={!currentUser?.id}
                  >
                    Actualiser
                  </Button>
                </div>
              </div>

              {loadingInvoices ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
                </div>
              ) : filteredInvoiceRows.length === 0 ? (
                <div className="bg-card border border-border rounded-lg p-10 text-center">
                  <Icon name="Receipt" size={42} className="text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">Aucune facture envoyée trouvée pour vos filtres.</p>
                </div>
              ) : (
                <div className="bg-card border border-border rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted/50 border-b border-border">
                        <tr>
                          <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">Facture</th>
                          <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">Cas</th>
                          <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">Patient</th>
                          <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">Phase</th>
                          <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">Créé</th>
                          <th className="px-6 py-4 text-right text-sm font-semibold text-foreground">Total</th>
                          <th className="px-6 py-4 text-right text-sm font-semibold text-foreground">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {filteredInvoiceRows.map((row) => (
                          <tr key={`${row.caseId}-${row.invoice?.id}-${row.phaseNumber}`} className="hover:bg-muted/20">
                            <td className="px-6 py-4 text-sm text-foreground font-medium">
                              {row.invoice?.invoice_number || '—'}
                              <div className="text-xs text-muted-foreground">{formatDate(row.invoice?.issue_date)}</div>
                            </td>
                            <td className="px-6 py-4 text-sm text-foreground">
                              {row.caseNumber}
                              <div className="text-xs text-muted-foreground">{formatDate(row.caseCreatedDate)}</div>
                            </td>
                            <td className="px-6 py-4 text-sm text-foreground">
                              {row.patientName}
                              <div className="text-xs text-muted-foreground">{row.patientCode}</div>
                            </td>
                            <td className="px-6 py-4 text-sm text-foreground">
                              {row.phaseNumber}. {row.phaseName}
                            </td>
                            <td className="px-6 py-4 text-sm text-foreground">{formatDate(row.caseCreatedDate)}</td>
                            <td className="px-6 py-4 text-sm text-foreground text-right">
                              {formatCurrency(row.invoice?.total_amount, row.currency)}
                            </td>
                            <td className="px-6 py-4 text-right">
                              <Button
                                variant="outline"
                                size="sm"
                                iconName="Download"
                                iconPosition="left"
                                onClick={() => handleDownloadInvoicePdf(row.invoice, row)}
                              >
                                PDF
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === 'packs' && (
            <div className="space-y-4">
              {loadingPacks ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
                </div>
              ) : packsError ? (
                <div className="bg-card border border-border rounded-lg p-6">
                  <p className="text-destructive">{packsError}</p>
                </div>
              ) : packages.length === 0 ? (
                <div className="bg-card border border-border rounded-lg p-10 text-center">
                  <Icon name="Package" size={42} className="text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">Aucun pack disponible.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {packages.map((pkg) => {
                    const expanded = expandedPackageIds.has(pkg.id);
                    const currency = pkg.countries?.currency_code || currencyCode || 'EUR';
                    return (
                      <div key={pkg.id} className="bg-card border border-border rounded-lg overflow-hidden">
                        <button
                          type="button"
                          className="w-full text-left p-5 hover:bg-muted/10 transition-colors"
                          onClick={() => toggleExpanded(pkg.id)}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <h3 className="text-lg font-semibold text-foreground truncate">{pkg.package_name}</h3>
                                {pkg.tier && (
                                  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${getTierColor(pkg.tier)}`}>
                                    {pkg.tier}
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground">{pkg.package_code}</p>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <div className="text-right">
                                <div className="text-lg font-bold text-foreground">
                                  {formatCurrency(pkg.final_price ?? pkg.base_price ?? 0, currency)}
                                </div>
                                {pkg.included_units != null && (
                                  <div className="text-xs text-muted-foreground">{pkg.included_units} unités incluses</div>
                                )}
                              </div>
                              <Icon name="ChevronDown" size={18} className={`transition-transform ${expanded ? 'rotate-0' : '-rotate-90'}`} />
                            </div>
                          </div>
                        </button>
                        {expanded && (
                          <div className="border-t border-border p-5">
                            {pkg.description && <p className="text-sm text-muted-foreground mb-3">{pkg.description}</p>}
                            {(pkg.package_services || []).length === 0 ? (
                              <p className="text-sm text-muted-foreground">Aucun service associé.</p>
                            ) : (
                              <div className="space-y-2">
                                <div className="text-sm font-semibold text-foreground">Services inclus</div>
                                <div className="space-y-2">
                                  {(pkg.package_services || []).map((ps) => (
                                    <div key={ps.id} className="flex items-start justify-between gap-3 text-sm">
                                      <div className="min-w-0">
                                        <div className="font-medium text-foreground truncate">
                                          {ps.service?.service_name || 'Service'}
                                        </div>
                                        <div className="text-xs text-muted-foreground">{ps.service?.service_code}</div>
                                      </div>
                                      <div className="text-xs text-muted-foreground flex-shrink-0">x{ps.quantity ?? 1}</div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default DentistBillingPackPage;

